class BackgroundVideo extends HTMLElement {
  constructor() {
    super();

    this.video = this.querySelector("video");
    this.view = this.dataset.view;
    this.pauseButton = this.querySelector("background-video-pause-button");
    this.tracks = this.querySelectorAll("track");
    this.sourcesLoaded = false;
    this.isNearViewport = false;
    this.hasRenderedFrame = false;
    this.userPaused = false;
    this.playRequest = null;
    this.frameCallbackId = null;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.mobileViewport = window.matchMedia("(max-width: 1023px)");

    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    this.setupSubtitles();
    this.setupMuteButton();
    this.setupFrameDetection();
    this.setupViewportLoading();
  }

  setupViewportLoading() {
    this.mobileViewport.addEventListener?.("change", this.handleViewportChange);
    this.reducedMotion.addEventListener?.("change", this.handleViewportChange);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    if (this.reducedMotion.matches) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      this.isNearViewport = true;
      this.updateVideoPlayback();
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      this.handleIntersection,
      {
        rootMargin: "200px 0px",
        threshold: 0.01,
      }
    );
    this.intersectionObserver.observe(this);
  }

  handleIntersection(entries) {
    const entry = entries[0];
    this.isNearViewport = entry.isIntersecting;
    this.updateVideoPlayback();
  }

  handleViewportChange() {
    if (this.reducedMotion.matches) {
      this.video?.pause();
      return;
    }

    this.updateVideoPlayback();
  }

  handleVisibilityChange() {
    this.updateVideoPlayback();
  }

  isActiveView() {
    if (this.view === "all" || this.view == null) {
      return true;
    }

    return this.mobileViewport.matches
      ? this.view === "mobile"
      : this.view === "desktop";
  }

  loadSources() {
    if (!this.video || this.sourcesLoaded) {
      return;
    }

    this.video.querySelectorAll("source[data-src]").forEach((source) => {
      source.src = source.dataset.src;
    });

    this.sourcesLoaded = true;
    this.video.load();
  }

  setupFrameDetection() {
    if (!this.video) {
      return;
    }

    const revealAfterPaint = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => this.revealVideo());
      });
    };

    if ("requestVideoFrameCallback" in this.video) {
      this.frameCallbackId = this.video.requestVideoFrameCallback(() => {
        this.revealVideo();
      });
    } else {
      this.video.addEventListener("playing", revealAfterPaint, { once: true });
    }
  }

  revealVideo() {
    if (this.hasRenderedFrame) {
      return;
    }

    this.hasRenderedFrame = true;
    this.classList.add("bv-has-frame");
  }

  setupSubtitles() {
    if (!this.video || !this.tracks.length) {
      return;
    }

    this.video.addEventListener("loadedmetadata", () => {
      this.tracks.forEach((track) => {
        track.track.mode =
          track.srclang === window.Shopify?.locale ? "showing" : "hidden";
      });
    });
  }

  setupMuteButton() {
    this.muteBtn = this.parentElement?.parentElement?.querySelector(
      "button[aria-label='Mute'], button[aria-label='Unmute']"
    );

    if (!this.muteBtn) {
      return;
    }

    this.muteBtn.addEventListener("click", () => {
      if (!this.video || !this.isActiveView()) {
        return;
      }

      this.video.muted = !this.video.muted;
      this.muteBtn.setAttribute(
        "aria-label",
        this.video.muted ? "Mute" : "Unmute"
      );
      this.updateVideoPlayback();
    });
  }

  updateVideoPlayback() {
    if (!this.video) {
      return;
    }

    const shouldPlay =
      !this.reducedMotion.matches &&
      !document.hidden &&
      !this.userPaused &&
      this.isNearViewport &&
      this.isActiveView();

    if (!shouldPlay) {
      this.video.pause();
      return;
    }

    this.loadSources();

    if (!this.video.paused || this.playRequest) {
      return;
    }

    this.playRequest = this.video
      .play()
      .catch(() => {
        // Muted autoplay can still be blocked by browser or system settings.
      })
      .finally(() => {
        this.playRequest = null;
      });
  }

  pause() {
    this.userPaused = true;
    this.video?.pause();
    this.pauseButton?.setAttribute("data-playing", "false");
    this.pauseButton?.setAttribute(
      "aria-label",
      this.pauseButton.getAttribute("data-play")
    );
  }

  resume() {
    this.userPaused = false;
    this.updateVideoPlayback();
    this.pauseButton?.setAttribute("data-playing", "true");
    this.pauseButton?.setAttribute(
      "aria-label",
      this.pauseButton.getAttribute("data-pause")
    );
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
    this.mobileViewport.removeEventListener?.(
      "change",
      this.handleViewportChange
    );
    this.reducedMotion.removeEventListener?.(
      "change",
      this.handleViewportChange
    );
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );

    if (
      this.video &&
      this.frameCallbackId != null &&
      "cancelVideoFrameCallback" in this.video
    ) {
      this.video.cancelVideoFrameCallback(this.frameCallbackId);
    }
  }
}

customElements.define("background-video", BackgroundVideo);

class BackgroundVideoPauseButton extends HTMLElement {
  constructor() {
    super();

    this.bgVideo = this.closest("background-video");

    this.addEventListener("click", () => {
      if (!this.bgVideo?.video) {
        return;
      }

      if (!this.bgVideo.video.paused) {
        this.bgVideo.pause();
      } else {
        this.bgVideo.resume();
      }
    });
  }
}

customElements.define(
  "background-video-pause-button",
  BackgroundVideoPauseButton
);
